/**
 * DuplicateParcelModal — copy a parcel's research into another project (CT-020).
 *
 * Opens on `open-duplicate-parcel` (detail.parcelId); mounted once in OutlineView.
 * Copies the editable research inputs + zone/overlay/jurisdiction/association links
 * into a new Parcel under the chosen project + parcel number (formula fields
 * recompute in Airtable). Used for work that recurs under different project numbers.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { getActiveProjects } from '../services/commands.js'
import { duplicateParcel } from '../services/parcels.js'
import { alertDialog } from './Dialog.jsx'

export default function DuplicateParcelModal() {
  const [open, setOpen] = useState(false)
  const [parcelId, setParcelId] = useState('')
  const [projects, setProjects] = useState(null)
  const [targetProj, setTargetProj] = useState('')
  const [parcelNum, setParcelNum] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    function onOpen(e) {
      setParcelId((e && e.detail && e.detail.parcelId) || '')
      setTargetProj(''); setParcelNum(''); setError(''); setSaving(false)
      setOpen(true)
      if (!projects) getActiveProjects().then(setProjects).catch(() => setProjects([]))
    }
    window.addEventListener('open-duplicate-parcel', onOpen)
    return () => window.removeEventListener('open-duplicate-parcel', onOpen)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects])

  function close() { if (!saving) setOpen(false) }

  async function submit() {
    if (!parcelId) { setError('No parcel in context.'); return }
    if (!targetProj) { setError('Choose a target project.'); return }
    setSaving(true); setError('')
    try {
      const res = await duplicateParcel(parcelId, targetProj, parcelNum)
      if (res && res.error) throw new Error(typeof res.error === 'string' ? res.error : (res.error.message || 'Copy failed'))
      setOpen(false)
      await alertDialog({ title: 'Parcel copied', message: 'The parcel research was copied to the selected project.' })
    } catch (err) {
      setError((err && err.message) || 'Could not copy the parcel.')
      setSaving(false)
    }
  }

  if (!open) return null

  const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
  const input = { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
  const mono = { ...input, fontFamily: '"DM Mono",monospace' }
  const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="DuplicateParcelModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 440, maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>Duplicate Parcel</div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>Copy this parcel's research to another project.</div>
          </div>
          <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={label}>Project</label>
            <select style={input} value={targetProj} onChange={e => setTargetProj(e.target.value)}>
              <option value="">{projects ? 'Choose a project…' : 'Loading projects…'}</option>
              {(projects || []).map(p => (
                <option key={p.id} value={p.id}>
                  {[p.fields['Project Number'], p.fields['Project Name']].filter(Boolean).join(' — ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Project Parcel Number</label>
            <input style={mono} type="number" min="1" value={parcelNum} placeholder="1, 2, 3…" onChange={e => setParcelNum(e.target.value)} />
          </div>
          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={close} disabled={saving} style={{ ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: saving ? 0.6 : 1 }}>{saving ? 'Copying…' : 'Copy'}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}
